// QuickBooks connection status (2026-10-27). Accounting + admin users only.
//   useQboStatus(enabled) — checks /api/qbo/status on load and every 10 min
//   <QboChip>   — small "QuickBooks ●" indicator in the top bar (click = re-check)
//   <QboBanner> — red banner under the top bar when the connection is broken,
//                 with a Reconnect QuickBooks button
// The server also returns 403 for anyone else, so other users never see it.
import { useCallback, useEffect, useState } from 'react';

export function useQboStatus(enabled) {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const check = useCallback(async (force) => {
    if (!enabled) return;
    setChecking(true);
    try {
      const r = await fetch('/api/qbo/status' + (force ? '?force=1' : ''), { credentials: 'include' });
      if (r.status === 403) { setStatus(null); return; }
      const j = await r.json();
      setStatus(j && j.ok ? j : { connected: false, message: 'Could not check the QuickBooks connection.', error: (j && j.error) || r.status });
    } catch (e) {
      setStatus({ connected: false, message: 'Could not check the QuickBooks connection.', error: e.message });
    } finally { setChecking(false); }
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    check(false);
    const t = setInterval(() => check(false), 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [enabled, check]);
  return { status, checking, check };
}

const when = (ts) => {
  if (!ts) return 'never';
  const d = new Date(ts);
  return isNaN(d) ? String(ts) : d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

async function reconnect(setMsg) {
  setMsg('Opening QuickBooks…');
  try {
    const r = await fetch('/api/qbo-connect', { credentials: 'include' });
    if (!r.ok) throw new Error(await r.text());
    const j = await r.json();
    if (!j.authUrl) throw new Error('no sign-in link returned');
    window.location.href = j.authUrl;   // returns to the dashboard after sign-in
  } catch (e) { setMsg('Reconnect failed: ' + e.message); }
}

export function QboChip({ qbo }) {
  const s = qbo.status;
  if (!s) return null;
  const ok = !!s.connected;
  const title = ok
    ? 'QuickBooks connected' + (s.company ? ' to ' + s.company : '') + ' · checked ' + when(s.checkedAt) + ' · click to re-check'
    : 'QuickBooks NOT connected · last worked ' + when(s.lastOkAt) + ' · click to re-check';
  return (
    <a href="#" className="small" title={title}
       onClick={(e) => { e.preventDefault(); qbo.check(true); }}
       style={{ marginRight: '0.6rem', textDecoration: 'none', whiteSpace: 'nowrap',
                color: ok ? '#166534' : '#b91c1c', fontWeight: ok ? 500 : 700 }}>
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, marginRight: 4,
                     background: ok ? '#16a34a' : '#dc2626', verticalAlign: 'middle' }} />
      {qbo.checking ? 'QuickBooks…' : ok ? 'QuickBooks' : 'QuickBooks disconnected'}
    </a>
  );
}

export function QboBanner({ qbo }) {
  const [msg, setMsg] = useState('');
  const s = qbo.status;
  if (!s || s.connected) return null;
  return (
    <div style={{ background: '#fef2f2', borderBottom: '2px solid #dc2626', color: '#7f1d1d',
                  padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap' }}>
      <strong>⚠ QuickBooks is not connected.</strong>
      <span className="small">
        {s.message || 'QuickBooks could not be reached.'} New invoices, payments and vendor spend will be missing until it is reconnected.
        {' '}Last worked: {when(s.lastOkAt)}.
      </span>
      <button className="refresh-btn" onClick={() => reconnect(setMsg)}
              style={{ background: '#dc2626', color: '#fff', padding: '5px 12px' }}>
        🔌 Reconnect QuickBooks
      </button>
      <a href="#" className="small" onClick={(e) => { e.preventDefault(); qbo.check(true); }} style={{ color: '#7f1d1d' }}>
        {qbo.checking ? 'checking…' : 'check again'}
      </a>
      {msg && <span className="small">{msg}</span>}
      {s.error && <span className="small" style={{ fontFamily: 'monospace', color: '#9f1239', flexBasis: '100%' }}>{String(s.error).slice(0, 200)}</span>}
    </div>
  );
}
